import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, Paper, Stack,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
  MenuItem,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import UndoIcon from "@mui/icons-material/Undo";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  salesReturnsApi, salesInvoicesApi,
  type ReturnableLineDto, type CreateSalesReturnRequest,
} from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { PageHeader } from "@/components/PageHeader";
import { DetailSkeleton } from "@/components/Skeletons";
import { formatDate } from "@/shared/datetime";
import { useAuth } from "@/store/auth";

const inr = new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" });

const REFUND_MODES: CreateSalesReturnRequest["refundMode"][] =
  ["Cash", "Card", "Upi", "Credit"];

/**
 * Taking goods back against a posted bill (W8).
 *
 * The screen is built around what the server will allow rather than around what the bill says:
 * `returnable` already knows how much of each line has come back before, and why a line cannot be
 * returned at all — an expired batch, a schedule drug, a consultation. Showing that up front is
 * the difference between a counter that works and one where every second return is a rejection.
 */
export function SalesReturnPage() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();
  const canPost = useAuth(s => s.hasPermission("sales.return.post"));

  const [qtyByLine, setQtyByLine] = useState<Record<string, number>>({});
  const [reason, setReason] = useState("");
  const [refundMode, setRefundMode] = useState<CreateSalesReturnRequest["refundMode"]>("Cash");
  const [refundReference, setRefundReference] = useState("");

  const invoice = useQuery({
    queryKey: ["sales-invoice", invoiceId],
    queryFn: () => salesInvoicesApi.get(invoiceId!) as Promise<{ documentNo: string; documentDate: string }>,
    enabled: !!invoiceId,
  });

  const returnable = useQuery({
    queryKey: ["sales-returns", "returnable", invoiceId],
    queryFn: () => salesReturnsApi.returnable(invoiceId!),
    enabled: !!invoiceId,
  });

  const lines = returnable.data ?? [];
  const qtyFor = (l: ReturnableLineDto) => qtyByLine[l.salesInvoiceLineId] ?? 0;
  const creditTotal = lines.reduce((s, l) => s + qtyFor(l) * l.rate, 0);
  const anySelected = lines.some(l => qtyFor(l) > 0);

  const validationError =
    !anySelected ? "Enter a quantity on at least one line"
    : !reason.trim() ? "A return needs a reason"
    : lines.find(l => qtyFor(l) > l.qtyReturnable)
      ? "One line is above what remains returnable"
    : null;

  const mutation = useMutation({
    meta: { successMessage: "Return posted" },
    mutationFn: async () => {
      const created = await salesReturnsApi.create({
        salesInvoiceId: invoiceId!,
        reason: reason.trim(),
        refundMode,
        refundReference: refundReference.trim() || null,
        lines: lines
          .filter(l => qtyFor(l) > 0)
          .map(l => ({ salesInvoiceLineId: l.salesInvoiceLineId, qty: qtyFor(l) })),
      });
      // Created as a draft, then committed. Split so a tenant that wants a second pair of eyes on
      // refunds can withhold sales.return.post without blocking the counter from raising one.
      if (canPost) await salesReturnsApi.post(created.id);
      return created.id;
    },
    onSuccess: () => navigate(`/sales/invoices/${invoiceId}`),
  });

  if (returnable.isLoading) return <DetailSkeleton />;
  if (returnable.isError) {
    return <Alert severity="error">{errorMessage(returnable.error)}</Alert>;
  }

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Sales", "Returns"]}
        title={`Return against ${invoice.data?.documentNo ?? "invoice"}`}
        subtitle={invoice.data?.documentDate
          ? `Billed ${formatDate(invoice.data.documentDate)}`
          : undefined}
        actions={
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(`/sales/invoices/${invoiceId}`)}>
            Back to invoice
          </Button>
        }
      />

      {mutation.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(mutation.error)}</Alert>
      )}
      {!canPost && (
        <Alert severity="info" sx={{ mb: 2 }}>
          You can raise this return but not post it — someone with the posting permission will
          need to release the refund.
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 50 }}>#</TableCell>
              <TableCell>Item</TableCell>
              <TableCell>Batch</TableCell>
              <TableCell align="right">Sold</TableCell>
              <TableCell align="right">Already back</TableCell>
              <TableCell align="right">Returnable</TableCell>
              <TableCell align="right">Rate</TableCell>
              <TableCell align="right" sx={{ width: 120 }}>Return qty</TableCell>
              <TableCell align="right">Credit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map(l => {
              const blocked = !!l.blockedReason;
              return (
                <TableRow key={l.salesInvoiceLineId} sx={blocked ? { opacity: 0.55 } : undefined}>
                  <TableCell>{l.lineNo}</TableCell>
                  <TableCell>
                    <Stack>
                      <strong>{l.sku ?? l.itemName ?? l.lineType}</strong>
                      <Typography variant="caption" color="text.secondary">{l.itemName}</Typography>
                      {blocked && (
                        <Chip size="small" color="default" variant="outlined"
                          label={l.blockedReason} sx={{ mt: 0.5, alignSelf: "flex-start" }} />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {l.batchNo ?? "—"}
                    {l.expiryDate && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        exp {formatDate(l.expiryDate)}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{l.qtySold}</TableCell>
                  <TableCell align="right">{l.qtyReturned || "—"}</TableCell>
                  <TableCell align="right">{l.qtyReturnable}</TableCell>
                  <TableCell align="right">{inr.format(l.rate)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={l.blockedReason ?? ""}>
                      <span>
                        <TextField
                          type="number" size="small" disabled={blocked}
                          value={qtyByLine[l.salesInvoiceLineId] ?? ""}
                          onChange={(e) => setQtyByLine(q => ({
                            ...q, [l.salesInvoiceLineId]: Number(e.target.value) || 0,
                          }))}
                          error={qtyFor(l) > l.qtyReturnable}
                          inputProps={{
                            min: 0, max: l.qtyReturnable, step: "1",
                            style: { textAlign: "right" },
                          }}
                          sx={{ width: 100 }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell align="right">
                    {qtyFor(l) > 0 ? inr.format(qtyFor(l) * l.rate) : "—"}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            label="Reason *" size="small" fullWidth
            value={reason} onChange={(e) => setReason(e.target.value)}
            error={!reason.trim()}
            helperText="Recorded on the credit note — a return with no reason is a leak"
            inputProps={{ maxLength: 500 }}
          />
          <TextField
            select label="Refund via" size="small" sx={{ minWidth: 160 }}
            value={refundMode}
            onChange={(e) => setRefundMode(e.target.value as CreateSalesReturnRequest["refundMode"])}
          >
            {REFUND_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField
            label="Refund reference" size="small" sx={{ minWidth: 200 }}
            value={refundReference} onChange={(e) => setRefundReference(e.target.value)}
            inputProps={{ maxLength: 100 }}
          />
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <Stack direction="row" alignItems="center" spacing={2}>
          <Typography variant="subtitle1">
            Credit total: <b>{inr.format(creditTotal)}</b>
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => navigate(`/sales/invoices/${invoiceId}`)}>Cancel</Button>
          <Button
            variant="contained" color="warning"
            startIcon={mutation.isPending ? <CircularProgress size={16} color="inherit" /> : <UndoIcon />}
            disabled={!!validationError || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {canPost ? "Return & refund" : "Raise return"}
          </Button>
        </Stack>

        {validationError && (
          <Alert severity="info" sx={{ mt: 2 }}>{validationError}</Alert>
        )}
      </Paper>
    </Box>
  );
}
